import React, { Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchDistributorTransactionDemographics } from '../../../../services/Http/DistributorApi';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const DistributorTransactionsStatistics = ({ period }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const response = await fetchDistributorTransactionDemographics({ period });
                setData(response);
            } catch (error) {
                toast.error(t('DistributorTransactions.errors.loadDemographics'));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [period, t]);

    if (loading || !data) {
        return <Placeholder>{t('common.loading')}</Placeholder>;
    }

    const retailerSummary = data.retailer_summary || {};
    const regionSummary = data.region_summary || {};
    const venueSummary = data.venue_summary || {};

    const renderChart = (hasData, chart) => (
        hasData ? chart : <Placeholder>{t('DistributorTransactions.statistics.empty')}</Placeholder>
    );

    return (
        <Grid>
            <ChartCard>
                <h4>{t('DistributorTransactions.statistics.topRetailers')}</h4>
                <Suspense fallback={<Placeholder>…</Placeholder>}>
                    {renderChart(
                        Object.keys(retailerSummary).length,
                        <ReactApexChart
                            type="pie"
                            height={320}
                            options={{ labels: Object.keys(retailerSummary) }}
                            series={Object.values(retailerSummary)}
                        />
                    )}
                </Suspense>
            </ChartCard>
            <ChartCard>
                <h4>{t('DistributorTransactions.statistics.regions')}</h4>
                <Suspense fallback={<Placeholder>…</Placeholder>}>
                    {renderChart(
                        Object.keys(regionSummary).length,
                        <ReactApexChart
                            type="pie"
                            height={320}
                            options={{ labels: Object.keys(regionSummary) }}
                            series={Object.values(regionSummary)}
                        />
                    )}
                </Suspense>
            </ChartCard>
            <ChartCard>
                <h4>{t('DistributorTransactions.statistics.venues')}</h4>
                <Suspense fallback={<Placeholder>…</Placeholder>}>
                    {renderChart(
                        Object.keys(venueSummary).length,
                        <ReactApexChart
                            type="bar"
                            height={320}
                            options={{ xaxis: { categories: Object.keys(venueSummary) } }}
                            series={[{ name: t('DistributorTransactions.statistics.venues'), data: Object.values(venueSummary) }]}
                        />
                    )}
                </Suspense>
            </ChartCard>
        </Grid>
    );
};

export default DistributorTransactionsStatistics;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
`;

const ChartCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
`;

const Placeholder = styled.div`
    text-align: center;
    padding: 2.5rem;
`;
